import type { SignupSources } from "@repo/fika-shared/src/loader/user-schema.js"
import {
  createEffect,
  createMemo,
  createSignal,
  Match,
  type Setter,
  Show,
  Switch,
} from "solid-js"
import z from "zod"
import { createSession } from "@/context/session-provider"
import { http } from "@/data/http"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { OtpInput } from "@/designSystem/components/otp-input"
import { Text } from "@/designSystem/components/text"
import { Well } from "@/designSystem/components/well"
import { useI18nContext } from "@/i18n/i18n-solid"
import { betterAuthClient } from "@/lib/better-auth"
import {
  backButtonClass,
  dividerLineClass,
  dividerLineContainerClass,
  emailStepContainerClass,
  googleLogoClass,
  inlineButtonClass,
  inlineFormContainerClass,
  inlineInputClass,
  noticeBoxClass,
} from "./auth-flow.css"
import GoogleLogoSvg from "./google-logo.svg?raw"
import LinkedInLogoSvg from "./linkedin.svg?raw"

const subscribeSignupResponse = z.object({
  action: z.enum(["signed_up", "otp_sent"]),
})

export type AuthFlowStep = "email" | "otp"

interface AuthFlowProps {
  initialEmail?: string
  initialStep?: AuthFlowStep
  showSocialLogin?: boolean
  submitButtonLabel?: string
  signupSource?: SignupSources
  frictionless?: boolean
  isSuspended?: boolean
  onSuccess?: () => void
}

export function AuthFlow(props: AuthFlowProps) {
  const [step, setStep] = createSignal<AuthFlowStep>(
    props.initialStep ?? "email",
  )
  const [email, setEmail] = createSignal(props.initialEmail ?? "")
  const signupSource = createMemo(() => props.signupSource ?? "writer")
  const session = createSession()

  async function handleFrictionlessSubmit(email: string) {
    const res = await http("/api/subscribe-signup", {
      method: "POST",
      body: JSON.stringify({ email }),
      headers: { "Content-Type": "application/json" },
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = subscribeSignupResponse.parse(await res.json())
    if (data.action === "signed_up") {
      await session.actions.refetchSession()
      props.onSuccess?.()
      return "done" as const
    }
    return "otp" as const
  }

  return (
    <Switch>
      <Match when={step() === "email"}>
        <EmailStep
          email={email()}
          setEmail={setEmail}
          onSuccess={() => setStep("otp")}
          showSocialLogin={props.showSocialLogin}
          submitButtonLabel={props.submitButtonLabel}
          signupSource={signupSource()}
          frictionless={props.frictionless}
          isSuspended={props.isSuspended}
          onFrictionlessSubmit={handleFrictionlessSubmit}
        />
      </Match>
      <Match when={step() === "otp"}>
        <OtpStep
          email={email()}
          onBackToEmail={() => setStep("email")}
          onSuccess={async () => {
            await session.actions.refetchSession()
            props.onSuccess?.()
          }}
          signupSource={signupSource()}
        />
      </Match>
    </Switch>
  )
}

interface EmailStepProps {
  email: string
  setEmail: Setter<string>
  onSuccess: () => void
  showSocialLogin?: boolean
  submitButtonLabel?: string
  signupSource: SignupSources
  frictionless?: boolean
  isSuspended?: boolean
  onFrictionlessSubmit?: (email: string) => Promise<"done" | "otp">
}

function EmailStep(props: EmailStepProps) {
  const { LL } = useI18nContext()
  const [isLoading, setIsLoading] = createSignal(false)
  const [isGoogleLoading, setIsGoogleLoading] = createSignal(false)
  const [isLinkedInLoading, setIsLinkedInLoading] = createSignal(false)
  const [errorMessage, setErrorMessage] = createSignal("")

  async function submit() {
    setIsLoading(true)
    setErrorMessage("")
    try {
      if (props.frictionless && props.onFrictionlessSubmit) {
        const result = await props.onFrictionlessSubmit(props.email)
        if (result === "otp") {
          props.onSuccess()
        }
        return
      }
      await betterAuthClient().emailOtp.sendVerificationOtp({
        email: props.email,
        type: "sign-in",
      })
      props.onSuccess()
    } catch (e) {
      console.error(e)
      const errorMsg = LL().auth.failedToSend()
      setErrorMessage(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  async function handleSubmit(e: Event) {
    e.preventDefault()
    await submit()
  }

  async function handleGoogleSignIn() {
    setIsGoogleLoading(true)
    const callbackURL = window.location.href
    await betterAuthClient().signIn.social({
      provider: "google",
      callbackURL,
      fetchOptions: {
        query: { signupSource: props.signupSource },
      },
    })
  }

  async function handleLinkedInSignIn() {
    setIsLinkedInLoading(true)
    const callbackURL = window.location.href
    await betterAuthClient().signIn.social({
      provider: "linkedin",
      callbackURL,
      fetchOptions: {
        query: { signupSource: props.signupSource },
      },
    })
  }

  const isAnyLoading = createMemo(
    () => isGoogleLoading() || isLinkedInLoading() || isLoading(),
  )

  const showSocial = props.showSocialLogin ?? true

  return (
    <div class={emailStepContainerClass}>
      <Show when={props.isSuspended}>
        <Well hue="rose">
          <Box flexDirection="column" gap="4">
            <Text fontSize="s" fontWeight="500" color="rose800">
              {LL().auth.accountSuspendedTitle()}
            </Text>
            <Text fontSize="s" color="rose700">
              {LL().auth.accountSuspendedMessage()}
            </Text>
          </Box>
        </Well>
      </Show>

      <Show when={showSocial}>
        <Box gap="16">
          <Button
            hue="sand"
            kind="surface"
            onClick={handleGoogleSignIn}
            disabled={isAnyLoading()}
          >
            <Show when={!isGoogleLoading()}>
              <div class={googleLogoClass} innerHTML={GoogleLogoSvg} />
            </Show>
            {isGoogleLoading()
              ? LL().auth.signingIn()
              : LL().auth.continueWithGoogle()}
          </Button>

          <Button
            hue="sand"
            kind="surface"
            onClick={handleLinkedInSignIn}
            disabled={isAnyLoading()}
          >
            <Show when={!isLinkedInLoading()}>
              <div class={googleLogoClass} innerHTML={LinkedInLogoSvg} />
            </Show>
            {isLinkedInLoading()
              ? LL().auth.signingIn()
              : LL().auth.continueWithLinkedIn()}
          </Button>
        </Box>

        <div class={dividerLineContainerClass}>
          <div class={dividerLineClass} />
          {LL().auth.or()}
          <div class={dividerLineClass} />
        </div>
      </Show>

      <form onSubmit={handleSubmit}>
        <Box flexDirection="column" gap="16">
          <div class={inlineFormContainerClass}>
            <input
              type="email"
              class={inlineInputClass}
              placeholder={LL().auth.enterYourEmail()}
              value={props.email}
              onInput={e => props.setEmail(e.currentTarget.value)}
              required
              disabled={isAnyLoading()}
            />
            <button
              type="submit"
              class={inlineButtonClass}
              disabled={isAnyLoading()}
            >
              {isLoading()
                ? LL().auth.sending()
                : (props.submitButtonLabel ?? LL().onboarding.continue())}
            </button>
          </div>

          <Show when={errorMessage()}>
            <Box alignItems="center" flexDirection="column">
              <Text fontSize="s" color="rose900">
                {errorMessage()}
              </Text>
            </Box>
          </Show>
        </Box>
      </form>
    </div>
  )
}

interface OtpStepProps {
  email: string
  onSuccess?: () => void
  onBackToEmail?: () => void
  signupSource: SignupSources
}

function OtpStep(props: OtpStepProps) {
  const { LL } = useI18nContext()
  const [otp, setOtp] = createSignal("")
  const [isLoading, setIsLoading] = createSignal(false)
  const [errorMessage, setErrorMessage] = createSignal("")
  let otpInputRef: HTMLInputElement | undefined

  function handleOtpChange(value: string) {
    setOtp(value)
    if (errorMessage()) {
      setErrorMessage("")
    }
  }

  createEffect(() => {
    setTimeout(() => otpInputRef?.focus(), 100)
  })

  async function handleVerifyOTP() {
    setIsLoading(true)
    setErrorMessage("")

    try {
      const result = await betterAuthClient().signIn.emailOtp({
        email: props.email,
        otp: otp(),
        fetchOptions: {
          query: { signupSource: props.signupSource },
        },
      })

      if (result?.error) {
        throw new Error(result.error.message || "Verification failed")
      }

      props.onSuccess?.()
    } catch (err: unknown) {
      const errorMsg =
        err instanceof Error && err.message
          ? err.message
          : LL().auth.invalidCode()
      setErrorMessage(errorMsg)
      setOtp("")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box flexDirection="column" gap="24">
      <div class={noticeBoxClass}>
        <Text fontSize="m" color="sand700">
          {LL().auth.enterCode()} <strong>{props.email}</strong>
        </Text>
      </div>

      <OtpInput
        value={otp()}
        onValueChange={handleOtpChange}
        onComplete={handleVerifyOTP}
        disabled={isLoading()}
        error={!!errorMessage()}
        ref={otpInputRef}
      />

      <Show when={isLoading()}>
        <Box alignItems="center">
          <Text fontSize="m" color="sand600">
            {LL().auth.verifying()}
          </Text>
        </Box>
      </Show>

      <Show when={errorMessage()}>
        <Box alignItems="center">
          <Text fontSize="s" color="rose900">
            {errorMessage()}
          </Text>
        </Box>
      </Show>
      <Box justifyContent="center">
        <button
          type="button"
          onClick={props.onBackToEmail}
          class={backButtonClass}
        >
          {LL().auth.useDifferentEmail()}
        </button>
      </Box>
    </Box>
  )
}
